export const interviewQuestions = [
    // ─── 1. URL Shortener ────────────────────────────────────────
    {
        id: 'design-url-shortener',
        title: 'Design a URL Shortener (TinyURL / Bitly)',
        difficulty: 'Easy',
        summary: 'A classic question covering hash generation, Base62 encoding, read-heavy caching, collision handling, and NoSQL storage at scale.',
        content: `
## 1. Functional Requirements
1. Given a long URL, the service should generate a shorter, unique alias (e.g., \\\`https://tiny.url/3xK9zQ\\\`).
2. When users click a short link, they should be redirected to the original long URL.
3. Links should expire after a configurable TTL (default: 5 years).
4. Users can optionally pick a custom short alias (e.g., \\\`tiny.url/my-resume\\\`).
5. Analytics: track click counts per URL (optional but commonly asked).

## 2. Non-Functional Requirements
1. **Highly Available** — if the service goes down, millions of existing links across the internet break permanently. Availability is more important than consistency here.
2. **Low Latency** — URL redirection must happen in real time. Target: < 50ms p99 for reads. Users clicking links should never perceive a delay.
3. **Non-Guessable** — short URLs should not be predictable or sequential so users cannot enumerate them to discover private links.
4. **Scale Estimates:**
   - **Writes:** 100M new URLs/month = ~40 URLs/sec
   - **Read/Write Ratio:** 100:1 (reads vastly dominate) = ~4,000 reads/sec
   - **Storage:** Keeping URLs for 10 years: 100M x 12 x 10 = 12 billion records. At ~500 bytes/record = **6 TB total storage**
   - **Bandwidth:** 4,000 reads/sec x 500 bytes = 2 MB/sec outbound. Very manageable.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **URLMapping** | short_hash (PK), original_url, created_at, expires_at, user_id | The central table |
| **User** (optional) | user_id, api_key, email | Only if we support user accounts |
| **Analytics** (optional) | short_hash, click_count, last_accessed, referrer_counts | Click tracking |

## 4. API Design

### Create Short URL
\\\`POST /api/v1/shorten\\\`

Request body: \\\`{ "longUrl": "https://example.com/very-long-path", "customAlias": "optional", "expiresIn": "30d" }\\\`

Response: \\\`{ "shortUrl": "https://tiny.url/3xK9zQ", "expiresAt": "2027-01-01T00:00:00Z" }\\\`

### Redirect
\\\`GET /{shortHash}\\\` → HTTP 301 Redirect to the original URL

**Why 301 vs 302?**
- **301 (Permanent Redirect):** The browser caches the mapping. Subsequent clicks skip our servers entirely. Better for performance but we lose analytics on repeat visits.
- **302 (Temporary Redirect):** The browser always hits our server first. Essential if we want to track every single click. Most services use 302 for analytics.

## 5. High-Level Design

<diagram>
graph TD
    Client((Client)) --> LB[Load Balancer]
    LB --> API1[API Server 1]
    LB --> API2[API Server 2]
    API1 --> Cache[(Redis Cache Layer)]
    API2 --> Cache
    Cache -- Cache Miss --> DB[(NoSQL Database)]
    API1 --> IDGen[ID Generator Service]
    API2 --> IDGen
</diagram>

### The Write Path (Creating a Short URL)
1. Client sends \\\`POST /api/v1/shorten\\\` with the long URL.
2. The API server requests a unique integer ID from the ID Generator service.
3. The integer is converted to a Base62 string (the short hash).
4. The mapping \\\`{ short_hash → long_url }\\\` is stored in the NoSQL database.
5. The short URL is returned to the client.

### The Read Path (Redirecting)
1. Client hits \\\`GET /3xK9zQ\\\`.
2. API server checks Redis cache first. If cache hit, redirect immediately (< 5ms).
3. On cache miss, query the NoSQL database. Store the result in Redis for future hits.
4. Return HTTP 301/302 redirect to the original URL.

### URL Generation: Base62 Encoding
Base62 uses the character set \\\`[a-z, A-Z, 0-9]\\\` = 62 characters. A **7-character** Base62 string can represent 62^7 = **~3.5 trillion** unique URLs. This is more than enough for our 12-billion-record estimate.

**How to get the unique integer?** Using a standalone RDBMS auto-increment ID creates a single point of failure. Instead:
- **Twitter Snowflake:** Each server independently generates 64-bit IDs containing a timestamp, machine ID, and sequence number. No coordination needed.
- **ZooKeeper Range Allocation:** ZooKeeper assigns each API server a non-overlapping range of integers (e.g., Server 1 gets 1-1M, Server 2 gets 1M-2M). Each server increments locally within its range.

### Database Choice: NoSQL (Cassandra or DynamoDB)
Why NoSQL over SQL?
- **12 billion records, 6 TB of data** — horizontal scaling is critical.
- **No relationships** between records — no JOINs, no foreign keys needed.
- **Simple access pattern:** Single key lookup (\\\`short_hash → long_url\\\`). Perfect for a key-value store.
- Cassandra or DynamoDB provide single-digit-millisecond reads at any scale.

## 6. Optimizations

### Optimization 1: Tiered Caching with LRU
URL access patterns follow a power-law distribution: a tiny fraction of URLs (viral content, trending tweets) receive the vast majority of traffic. Place a **Redis cache** in front of the database with an LRU (Least Recently Used) eviction policy. Expected cache hit ratio: **> 95%**. This reduces database reads by 20x and keeps p99 latency under 10ms.

For extremely hot URLs, consider a second cache tier in the API server's local memory (in-process cache with TTL). This eliminates even the Redis network hop for the hottest ~1,000 URLs.

### Optimization 2: Database Sharding via Consistent Hashing
As data grows beyond a single machine, shard the database based on the hash of the short URL (e.g., first character: a-m → Shard 1, n-z → Shard 2). Use **Consistent Hashing** to distribute data evenly and handle node additions/removals gracefully without massive data migration. Each shard handles a proportional slice of the 4,000 reads/sec.

### Optimization 3: Rate Limiting URL Creation
Without limits, a malicious actor could flood the system with billions of junk URLs, exhaust the ID space, and bloat the database. Implement per-API-key rate limiting using a **Token Bucket** algorithm at the API Gateway layer (e.g., max 100 URLs/hour for free tier, 10,000/hour for paid tier).

### Optimization 4: Cleanup Service for Expired URLs
A background cron job runs every hour, querying for records where \\\`expires_at < NOW()\\\` and deleting them from both the database and the Redis cache. Use a **secondary index on expires_at** (or a TTL column in DynamoDB) for efficient querying. This reclaims storage and keeps the database lean.
    `
    },

    // ─── 2. Chat Application ────────────────────────────────────
    {
        id: 'design-chat-app',
        title: 'Design a Chat Application (WhatsApp / Slack)',
        difficulty: 'Hard',
        summary: 'Real-time bidirectional communication with WebSockets, message ordering, delivery receipts, presence detection, and group messaging at massive scale.',
        content: `
## 1. Functional Requirements
1. **1-on-1 Private Messaging** — two users can exchange text messages in real-time.
2. **Group Chats** — support groups of up to 500 members with shared message history.
3. **Real-Time Delivery** — messages must be delivered instantly when both users are online.
4. **Offline Support** — messages sent to offline users must be stored and delivered when they reconnect.
5. **Read Receipts & Delivery Status** — show sent, delivered, and read indicators (single tick, double tick, blue tick).
6. **Presence Indicator** — show online/offline/last-seen status for each user.
7. **Media Sharing** — support images, videos, and files up to 100MB.

## 2. Non-Functional Requirements
1. **Ultra-Low Latency** — message delivery in < 100ms when both users are online on a good network.
2. **Message Ordering** — messages in a conversation must always appear in the correct chronological order, even under high concurrency.
3. **Exactly-Once Delivery** — a message should never be duplicated or silently lost.
4. **High Availability** — 99.99% uptime. Chat is a primary communication channel; downtime is extremely disruptive.
5. **Scale Estimates:**
   - **50M Daily Active Users (DAU)**, average 40 messages/day = 2 billion messages/day
   - **500K concurrent WebSocket connections per server** (with proper OS tuning)
   - **Peak:** 5x average during events = 100K messages/sec

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **User** | user_id, name, avatar_url, status, last_seen | User profile |
| **Conversation** | conversation_id, type (1:1 or group), participants[], created_at | A chat thread |
| **Message** | message_id, conversation_id, sender_id, content, type (text/media), status (sent/delivered/read), created_at | Each individual message |
| **GroupMeta** | conversation_id, name, admin_ids[], max_members | Group-specific metadata |

## 4. API Design

### Protocol Choice: WebSocket
HTTP is **request-response** only — the client must ask the server "do I have new messages?" repeatedly (polling). This is wildly inefficient for chat.

**WebSocket** establishes a persistent, full-duplex TCP connection. Either side can push data at any time. This is how every modern chat app works.

- **ws://chat.app/connect?token=JWT** — Establish persistent WebSocket connection at login
- Message frame format: \\\`{ type: "message", conversationId: "abc", content: "Hello!", clientMsgId: "uuid" }\\\`

### REST Endpoints (for non-realtime operations)
- \\\`POST /api/v1/groups\\\` — create a group
- \\\`GET /api/v1/messages?conversationId=X&before=timestamp&limit=50\\\` — paginated message history (cursor-based)
- \\\`PUT /api/v1/users/profile\\\` — update profile

## 5. High-Level Design

<diagram>
graph TD
    UserA((User A)) <-->|WebSocket| CS1[Chat Server 1]
    UserB((User B)) <-->|WebSocket| CS2[Chat Server 2]

    CS1 --> Session[(Redis: Session Store)]
    CS2 --> Session

    CS1 <-.->|Redis Pub/Sub| CS2

    CS1 --> MQ[Kafka Message Queue]
    CS2 --> MQ

    MQ --> PersistSvc[Persistence Service]
    PersistSvc --> DB[(Cassandra)]

    MQ --> PushSvc[Push Notification Service]
    PushSvc --> APNS[Apple APNS]
    PushSvc --> FCM[Firebase FCM]
</diagram>

### The Core Message Flow (User A → User B, both online)
1. User A is connected to **Chat Server 1 (CS1)** via WebSocket.
2. User A sends a message for User B. CS1 receives it.
3. CS1 queries the **Redis Session Store**: "Which chat server is User B connected to?" → Answer: Chat Server 2 (CS2).
4. CS1 publishes the message to a **Redis Pub/Sub** channel that CS2 is subscribed to.
5. CS2 receives the message and pushes it to User B via WebSocket.
6. Simultaneously, CS1 publishes the message to **Kafka** for asynchronous persistence to Cassandra.

### What if User B is Offline?
- CS1 queries Redis Session Store: User B has no active connection.
- The message is persisted to Cassandra immediately (via Kafka pipeline).
- The message is also sent to the **Push Notification Service**, which sends a mobile push via APNS/FCM.
- When User B reconnects, the client fetches all undelivered messages from Cassandra via the REST API (cursor-based pagination using the last-seen message_id).

### Database Design: Cassandra
Chat generates a massive volume of small writes — billions per day. Cassandra is purpose-built for this: it has extremely fast sequential writes (append-only LSM tree) and scales horizontally across hundreds of nodes.

**Table Schema:**
- **Partition Key:** \\\`conversation_id\\\` — all messages in a conversation live on the same Cassandra node for efficient reads.
- **Clustering Key:** \\\`message_id DESC\\\` — messages are physically stored in reverse chronological order, so "load recent messages" is a sequential disk read.

## 6. Optimizations

### Optimization 1: Snowflake Message IDs for Ordering
Auto-increment IDs require a central counter — a bottleneck. UUID v4 is random and not sortable. Instead, use **Snowflake IDs**: 64-bit integers that embed a millisecond timestamp in the high bits. This guarantees: (a) global uniqueness without coordination and (b) chronological sortability, which Cassandra uses as the clustering key.

### Optimization 2: Presence with Heartbeat + Pull Model
Managing online/offline status for 50M users is expensive. The naive approach (User A comes online → broadcast to all 500 friends) creates a fanout storm.

**Solution:**
- **Heartbeat:** The client sends a "ping" to the Presence Service every 5 seconds. If 6 consecutive heartbeats are missed (30 seconds), the user is marked offline.
- **Pull Model:** When User B opens a chat, B's client fetches the current status only for the users visible on screen. No global broadcast. This reduces presence-related traffic by 100x.

### Optimization 3: Group Message Delivery
For a group with 500 members, do NOT send 500 separate messages over 500 WebSocket connections. Instead:
- Write ONE message record to Cassandra under the group's \\\`conversation_id\\\` partition.
- Send a lightweight "new message" notification via Redis Pub/Sub to all online group members' chat servers.
- Each member's client fetches the message from the group partition (or it's pushed via WebSocket to online users).

### Optimization 4: Media Upload with Pre-Signed URLs
Images and videos should NOT flow through the chat servers (they would choke on large files). Instead:
1. Client requests a **Pre-Signed S3 URL** from the API.
2. Client uploads the file **directly to S3** (bypassing chat servers entirely).
3. Client sends a chat message containing only the S3 object URL.
4. Receiving client downloads the media directly from S3/CDN.
This keeps chat servers handling only tiny text frames.

### Optimization 5: End-to-End Encryption (E2EE)
Each user generates a public/private key pair. Messages are encrypted on the sender's device using the recipient's public key and decrypted only on the recipient's device. The server never sees plaintext content. This is how WhatsApp's Signal Protocol works.
    `
    },

    // ─── 3. News Feed ───────────────────────────────────────────
    {
        id: 'design-news-feed',
        title: 'Design a News Feed (Twitter / Facebook)',
        difficulty: 'Hard',
        summary: 'The infamous fan-out problem: push vs pull models, the celebrity/hotkey dilemma, hybrid timeline generation, and feed caching strategies.',
        content: `
## 1. Functional Requirements
1. Users can **publish posts** (text, images, videos).
2. Users can **follow** other users.
3. Users see a **Home Feed** containing posts from people they follow, ordered by time or relevance.
4. Users can **like, comment, and share** posts.
5. The feed supports infinite scroll with cursor-based pagination.

## 2. Non-Functional Requirements
1. **Feed Generation < 200ms** — users expect the feed to load instantly when opening the app. Anything over 500ms feels broken.
2. **Highly Available** — the feed is the core product experience. It must always render, even if slightly stale.
3. **Eventual Consistency is acceptable** — it is fine if a new post takes 5-10 seconds to appear in all followers' feeds, but not 5 minutes.
4. **Scale Estimates:**
   - **500M Monthly Active Users (MAU)**, 200M DAU
   - Average user follows 300 people
   - Average user publishes 2 posts/week
   - **Celebrity problem:** Some users have 50M+ followers (Elon Musk, Taylor Swift)

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **User** | user_id, name, profile_image, follower_count | User profile |
| **Post** | post_id, author_id, content, media_urls[], created_at | A single post |
| **Follow** | follower_id, followee_id, created_at | Social graph edge |
| **FeedItem** | user_id, post_id, score, created_at | Pre-computed feed entry |

## 4. API Design
- \\\`POST /api/v1/posts\\\` — publish a new post (body: content, media files)
- \\\`GET /api/v1/feed?cursor=last_post_id&limit=20\\\` — fetch paginated home feed
- \\\`POST /api/v1/follow/{userId}\\\` — follow a user
- \\\`POST /api/v1/posts/{id}/like\\\` — like a post

**Cursor-based pagination** is essential here (not offset-based). The feed is constantly changing; offset pagination would cause duplicate or missed posts. The cursor is the last-seen post_id/timestamp.

## 5. High-Level Design

<diagram>
graph TD
    User((User)) --> LB[Load Balancer]
    LB --> PostSvc[Post Service]
    LB --> FeedSvc[Feed Retrieval Service]

    PostSvc --> PostDB[(Post Storage)]
    PostSvc --> Fanout[Fanout Service]

    Fanout --> GraphDB[(Social Graph: Who follows whom)]
    Fanout --> FeedCache[(Redis: Feed Cache per user)]

    FeedSvc --> FeedCache
    FeedSvc --> PostDB
    FeedSvc --> PostCache[(Redis: Post Cache)]
</diagram>

### The Two Paths
1. **Post Publishing Path:** User publishes → save to Post DB → trigger Fanout Service to distribute the post to followers' feeds.
2. **Feed Retrieval Path:** User opens app → Feed Service reads the user's pre-computed feed from Redis → enriches post_ids with full post details from Post Cache/DB → returns to client.

### The Fan-Out Problem (THE Critical Design Decision)

This is the heart of the interview question. When User A publishes a post, how does it reach all of User A's followers?

**Approach 1: Fan-out-on-Write (Push Model)**
When User A posts, the Fanout Service looks up ALL of User A's followers and **pushes** the post_id into each follower's feed cache (a Redis Sorted Set).
- **Pro:** Feed retrieval is incredibly fast — O(1), just read the pre-built list from Redis.
- **Con:** The **"Justin Bieber Problem"** — if a celebrity with 100M followers posts, the system must perform 100 million Redis write operations. This takes minutes and creates enormous write amplification.

**Approach 2: Fan-out-on-Read (Pull Model)**
When User B opens the app, the system looks up everyone B follows, queries the Post DB for their recent posts, and **merges + sorts** them on the fly.
- **Pro:** Publishing is O(1) fast. No fanout delay.
- **Con:** Feed retrieval is extremely slow. If you follow 300 people, the system must query 300 users' posts and merge-sort them every time you open the app. Unacceptable latency at scale.

## 6. Optimizations

### Optimization 1: Hybrid Fan-Out Model (The Industry Standard Answer)
Combine both approaches for the best of both worlds:
- **For normal users (< 100K followers):** Use Fan-out-on-Write. Push their posts directly into each follower's Redis feed cache.
- **For celebrities (> 100K followers):** Do NOT fan out. Skip them during the push phase.
- **At read time:** When a user loads their feed, the Feed Service: (1) reads the pre-computed cache (containing posts from normal friends), (2) queries the Post DB for recent posts from any celebrities the user follows, (3) **merges the two lists** and ranks them.

This means only a handful of extra DB queries at read time (the number of celebrities a user follows is usually < 20), while avoiding 100M-write fanout storms.

### Optimization 2: Feed Cache Structure and Eviction
Store each user's feed as a **Redis Sorted Set**: \\\`ZADD feed:{userId} {timestamp} {postId}\\\`. Limit the set to the most recent 800 entries (nobody scrolls past 800 posts). On read, \\\`ZREVRANGE feed:{userId} 0 19\\\` returns the 20 most recent post_ids. Then batch-fetch full post details from a **Post Cache** (Redis hash) or the Post DB via \\\`MGET\\\`.

### Optimization 3: Ranking and Relevance
Replace pure chronological sorting with a relevance-weighted score:
\\\`score = f(recency, engagement_rate, author_closeness, content_type)\\\`
- **recency:** Time decay function (newer = higher score)
- **engagement_rate:** Posts with high like/comment velocity score higher
- **author_closeness:** Users you interact with frequently are weighted higher
- **content_type:** Videos and images may score higher than plain text

Pre-compute the relevance score during fan-out and use it as the Redis ZSET score instead of raw timestamp.

### Optimization 4: Social Graph Storage
The follower/following relationship is a graph problem. Two storage options:
- **Adjacency list in Redis:** \\\`SET followers:{userId}\\\`. Fast O(1) lookup for "get all followers of user X." Good for fan-out.
- **Graph database (Neo4j):** Better for complex queries like "friends of friends" or "mutual connections." Used for recommendation features.
    `
    },

    // ─── 4. Rate Limiter ────────────────────────────────────────
    {
        id: 'design-rate-limiter',
        title: 'Design an API Rate Limiter',
        difficulty: 'Medium',
        summary: 'Deep dive into Token Bucket, Leaky Bucket, Sliding Window Counter algorithms, distributed state management with Redis, and race condition prevention with Lua scripts.',
        content: `
## 1. Functional Requirements
1. Limit the number of requests a user (or IP address) can send to an API within a specific time window (e.g., 100 requests per minute).
2. Rate limit rules should be **configurable per API endpoint** (e.g., /api/login allows 5 attempts/min, /api/data allows 1000/min).
3. Return **HTTP 429 (Too Many Requests)** with a \\\`Retry-After\\\` header when the limit is exceeded.
4. Support multiple limiting dimensions: per-IP (DDoS), per-user (abuse), per-API-key (tier enforcement).

## 2. Non-Functional Requirements
1. **Ultra-Low Latency** — the rate limiter sits in the critical path of EVERY API request. It must add < 5ms overhead. If it adds 100ms, every API call in the entire product is 100ms slower.
2. **Distributed Consistency** — with multiple API gateway servers, all must share state. A user cannot bypass the limit by having requests round-robin across different gateways.
3. **Fail-Open** — if the rate limiter's Redis cluster goes down, it should **allow traffic** (fail open) rather than block everything (fail closed). A broken rate limiter should not take down the entire API.
4. **Accuracy** — the system should not allow significantly more requests than the configured limit. Small inaccuracies (e.g., allowing 102 instead of 100) are acceptable.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **RateLimitRule** | endpoint, limit, window_seconds, algorithm | Configurable rules |
| **RateLimitCounter** | key (userId+endpoint composite), count, window_start, ttl | Runtime state — stored in Redis |

## 4. API Design
The rate limiter is **not a user-facing API** — it is **middleware** that intercepts every inbound request before it reaches the business logic.

Typically deployed as a component inside the **API Gateway** (Kong, AWS API Gateway, Envoy) or as a sidecar proxy.

**Response Headers (standard):**
- \\\`X-RateLimit-Limit: 100\\\` — configured limit
- \\\`X-RateLimit-Remaining: 42\\\` — remaining requests in this window
- \\\`X-RateLimit-Reset: 1625097600\\\` — Unix timestamp when the window resets
- \\\`Retry-After: 30\\\` — seconds until the client should retry (only on 429)

## 5. High-Level Design

<diagram>
graph LR
    Client --> GW[API Gateway with Rate Limiter]
    GW <-->|Atomic Check + Increment| Redis[(Redis Cluster)]
    GW --> RuleDB[(Rate Limit Rules Config)]
    GW -- Permitted --> Backend[Backend API Servers]
    GW -- Rejected --> R429[HTTP 429 Too Many Requests]
</diagram>

### The 5 Rate Limiting Algorithms (You MUST Know All of These)

**1. Token Bucket (Used by Amazon, Stripe, GitHub)**
Imagine a bucket that holds \\\`N\\\` tokens (the burst capacity). A "refiller" adds tokens at a constant rate (e.g., 10 tokens/second). Each request consumes 1 token. If the bucket is empty, the request is rejected.
- **Pros:** Allows bursts (up to bucket capacity), extremely memory efficient (just 2 numbers: token count + last refill timestamp).
- **Cons:** Slightly complex to implement correctly in a distributed system.

**2. Leaky Bucket**
Requests enter a FIFO queue (the "bucket"). The queue drains at a fixed, constant rate (like water leaking from a hole). If the queue is full, new requests are dropped.
- **Pros:** Produces perfectly smooth, uniform output traffic. Great for APIs that need strict rate control.
- **Cons:** Cannot handle legitimate traffic bursts. A sudden spike in valid traffic gets dropped even if average load is well under the limit.

**3. Fixed Window Counter**
Divide time into fixed windows (e.g., 1-minute intervals: 12:00-12:01, 12:01-12:02). Maintain a counter for each user per window. If counter > limit, reject.
- **Pros:** Simple to implement. Just one Redis INCR + EXPIRE.
- **Critical Problem:** The "Edge Spike" vulnerability. A user sends 100 requests at 12:00:59, and another 100 at 12:01:01. From the algorithm's perspective, both are within their respective windows. But from a real-world perspective, that is 200 requests in 2 seconds — double the intended limit.

**4. Sliding Window Log**
For every request, store the exact timestamp in a Redis Sorted Set. On each new request, remove all timestamps older than the window, count remaining entries. If count < limit, allow.
- **Pros:** Perfect accuracy. No edge-spike problem.
- **Cons:** Extremely memory intensive. Storing individual timestamps for millions of users x thousands of requests = billions of entries. Not practical at scale.

**5. Sliding Window Counter (Best of both worlds — recommended answer)**
A hybrid of Fixed Window and Sliding Window Log. It uses two adjacent fixed windows and computes a **weighted estimate:**
\\\`estimated_count = (previous_window_count * overlap_percentage) + current_window_count\\\`
Example: The window is 1 minute. We are at 12:01:15 (25% into the current window). Previous window had 84 requests. Current window has 36 requests.
\\\`estimated = 84 * 0.75 + 36 = 99\\\`. If limit is 100, allow the request.
- **Pros:** Minimal memory (just 2 counters), solves the edge-spike problem, and is highly accurate.

## 6. Optimizations

### Optimization 1: Redis Lua Scripts for Atomic Operations
**The problem:** Two API gateway instances read the same Redis counter simultaneously, both see "count = 99," both increment to 100, both allow the request. The actual count should be 101 — the user bypassed the limit.

**The solution:** Redis **Lua scripts** execute entirely on the Redis server as a single atomic operation. No other Redis command can interleave. Package the entire read-check-increment logic into one Lua script:
\\\`\\\`\\\`
local count = redis.call('INCR', KEYS[1])
if count == 1 then redis.call('EXPIRE', KEYS[1], ARGV[1]) end
if count > tonumber(ARGV[2]) then return 0 end
return 1
\\\`\\\`\\\`

### Optimization 2: Local In-Memory Counter Sync
For ultra-high-throughput APIs (millions of requests per second), even Redis becomes a bottleneck. Solution: maintain a **local in-memory counter** on each API gateway server. periodically sync (every 500ms) with Redis. Accept a small inaccuracy window (~0.5 seconds worth of requests) for massive throughput gains.

### Optimization 3: Multi-Dimensional Rate Limiting
Layer multiple rate limits to catch different abuse patterns:
- **Per-IP:** 1000 req/min. Catches DDoS attacks.
- **Per-User:** 100 req/min. Catches individual abuse.
- **Per-API-Key:** Enforced by subscription tier (free: 10 req/min, pro: 1000 req/min).
Each dimension is a separate Redis key. A request must pass ALL dimensions to be allowed.

### Optimization 4: Graceful Degradation with Fail-Open
If the Redis cluster becomes unreachable, the API gateway should switch to a local, in-memory rate limiter (best-effort), log an alert, and continue serving traffic. Blocking all traffic because the rate limiter is down is worse than allowing slightly more traffic than intended.
    `
    },

    // ─── 5. Notification System ─────────────────────────────────
    {
        id: 'design-notification-system',
        title: 'Design a Notification System',
        difficulty: 'Medium',
        summary: 'Multi-channel delivery (push, SMS, email), priority queues, user preferences, deduplication, templating, and retry strategies with dead letter queues.',
        content: `
## 1. Functional Requirements
1. Send notifications via three channels: **Push** (iOS/Android), **SMS**, and **Email**.
2. Support both **real-time** notifications (triggered by events) and **scheduled** notifications (send at a future time).
3. Users can configure per-channel preferences: opt-in/out for each notification type, set quiet hours (e.g., no notifications between 10pm-8am).
4. Support **notification templates** with variable substitution (e.g., "Hi {{name}}, your order #{{orderId}} has shipped").
5. Notification history: users can view all past notifications in-app.

## 2. Non-Functional Requirements
1. **Soft Real-Time** — push notifications should be delivered within 5 seconds of the triggering event under normal load.
2. **At-Least-Once Delivery** — a notification should never be silently dropped. If delivery fails, retry. Duplicates are tolerable (idempotent handling on the client side).
3. **Deduplication** — the system itself should never intentionally send the exact same notification twice due to retries or system errors.
4. **Scale:** 10 million notifications per day, with burst capacity of 1 million per hour during events (flash sales, breaking news).
5. **Extensibility** — adding a new channel (e.g., WhatsApp, Slack webhook) should require minimal changes.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **Notification** | id, user_id, type, channel, template_id, payload (JSON), status (queued/sent/failed), created_at, sent_at | Each notification event |
| **UserPreference** | user_id, channel, notification_type, enabled, quiet_start, quiet_end | Per-user opt-in/out |
| **Template** | template_id, channel, subject_template, body_template, variables[] | Reusable templates |
| **DeviceToken** | user_id, platform (ios/android), token, updated_at | For push delivery |

## 4. API Design
- \\\`POST /api/v1/notifications/send\\\` — { userId, templateId, channel, payload, scheduledAt? }
- \\\`POST /api/v1/notifications/bulk\\\` — { userIds[], templateId, channel, payload } (for mass notifications)
- \\\`GET /api/v1/notifications?userId=X&page=1\\\` — notification history
- \\\`PUT /api/v1/preferences\\\` — { userId, channel, type, enabled }

## 5. High-Level Design

<diagram>
graph TD
    Upstream[Upstream Services] --> API[Notification API]
    API --> Validate[Validation + Preference Check]
    Validate --> PQ[Priority Queue - Kafka]

    PQ --> PushW[Push Worker Pool]
    PQ --> SMSW[SMS Worker Pool]
    PQ --> EmailW[Email Worker Pool]

    PushW --> APNS[Apple APNS / Firebase FCM]
    SMSW --> Twilio[Twilio / AWS SNS]
    EmailW --> SES[AWS SES / SendGrid]

    PushW --> LogDB[(Notification Log DB)]
    SMSW --> LogDB
    EmailW --> LogDB
</diagram>

### The Processing Pipeline
1. An upstream service (Order Service, Auth Service, etc.) calls the Notification API.
2. The API validates the request, checks the user's **preferences** (is this channel enabled? are we in quiet hours?), and renders the **template** by substituting variables.
3. The notification is enqueued into Kafka with a priority level (HIGH for OTP codes, LOW for marketing).
4. Channel-specific **worker pools** consume from Kafka and send via third-party providers (APNS, Twilio, SES).
5. Delivery status is logged to the Notification Log DB for history and debugging.

## 6. Optimizations

### Optimization 1: Priority Queues
Not all notifications are equal. An OTP code for login is critical and time-sensitive. A weekly newsletter is not. Use **separate Kafka topics** for HIGH and LOW priority. Allocate more worker instances to the HIGH priority topic. This ensures OTP codes are delivered in 2 seconds even during a marketing email blast.

### Optimization 2: Deduplication with Redis
Before sending, compute a hash of \\\`user_id + template_id + payload\\\`. Check Redis: \\\`SET notification:{hash} EX 3600 NX\\\`. If the key already exists (meaning this exact notification was already sent within the last hour), skip it. This prevents duplicate notifications caused by upstream service retries.

### Optimization 3: Per-User Rate Limiting
A bug in an upstream service could trigger 10,000 identical notifications to one user in a second. Apply per-user rate limits: max 5 push notifications per minute, max 3 SMS per hour. This protects users from notification spam even when upstream services misbehave.

### Optimization 4: Retry with Exponential Backoff + Dead Letter Queue
If APNS or Twilio returns a transient error (5xx, timeout), retry with exponential backoff: 1s → 2s → 4s → 8s → 16s, with random jitter to avoid thundering herd. After 5 consecutive failures, move the notification to a **Dead Letter Queue (DLQ)** for manual inspection. Never retry indefinitely.

### Optimization 5: Scheduled Notification Worker
For future-scheduled notifications (\\\`scheduledAt: "2027-01-01T09:00:00Z"\\\`), store them in a database with a \\\`scheduled_at\\\` index. A separate **scheduler worker** runs every minute, queries for notifications where \\\`scheduled_at <= NOW() AND status = 'pending'\\\`, and enqueues them into Kafka for immediate processing.
    `
    },

    // ─── 6. Distributed Key-Value Store ─────────────────────────
    {
        id: 'design-key-value-store',
        title: 'Design a Distributed Key-Value Store',
        difficulty: 'Hard',
        summary: 'Consistent hashing, quorum consensus (W+R>N), vector clocks for conflict resolution, gossip protocol, Merkle trees for anti-entropy, and hinted handoff.',
        content: `
## 1. Functional Requirements
1. \\\`put(key, value)\\\` — store a key-value pair.
2. \\\`get(key)\\\` — retrieve the value for a given key.
3. \\\`delete(key)\\\` — remove a key-value pair.
4. Keys and values are opaque byte arrays. The system does not interpret them.

## 2. Non-Functional Requirements
1. **Highly Available** — the system must always accept writes, even during network partitions. This is an **AP system** in the CAP theorem (Availability + Partition Tolerance over Consistency).
2. **Tunable Consistency** — clients can choose their consistency level per request: ONE (fastest, weakest), QUORUM (balanced), ALL (strongest, slowest).
3. **Scalable** — add nodes without downtime, handle petabytes of data across hundreds of nodes.
4. **Fault Tolerant** — no single point of failure. Any node can go down without data loss.
5. **Scale:** Billions of key-value pairs, millions of operations per second.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **Node** | node_id, ip, port, token_range, status (up/down/suspect) | A server in the cluster |
| **DataRecord** | key, value, version (vector clock), timestamp | A single stored key-value pair |
| **VirtualNode** | v_node_id, physical_node_id, position_on_ring | For even distribution |

## 4. API Design
- \\\`PUT /kv/{key}\\\` — Body: { value, consistency: "QUORUM" } → { version }
- \\\`GET /kv/{key}?consistency=QUORUM\\\` → { value, version }
- \\\`DELETE /kv/{key}\\\` → 204 No Content

Alternatively, expose as a client library: \\\`store.put(key, value, ConsistencyLevel.QUORUM)\\\`

## 5. High-Level Design

<diagram>
graph TD
    Client --> Coordinator[Coordinator Node]

    subgraph Consistent Hash Ring
        N1((Node A))
        N2((Node B))
        N3((Node C))
        N4((Node D))
    end

    Coordinator -->|Write Replica 1| N1
    Coordinator -->|Write Replica 2| N2
    Coordinator -->|Write Replica 3| N3

    N1 <-.->|Gossip Protocol| N2
    N2 <-.->|Gossip Protocol| N3
    N3 <-.->|Gossip Protocol| N4
</diagram>

### Data Partitioning: Consistent Hashing
Hash each key to a position on a virtual ring (0 to 2^128). Walk clockwise from that position to find the first node — that is the **primary owner**. Replicate the data to the next **N-1 nodes** clockwise (where N is the replication factor, typically 3).

**Virtual Nodes:** Each physical node maps to multiple virtual positions on the ring (e.g., 256 virtual nodes per physical node). This ensures even data distribution even when physical nodes have different capacities.

### Quorum Consensus
- **N** = total replicas (e.g., 3)
- **W** = write quorum (minimum ACKs to confirm a write)
- **R** = read quorum (minimum nodes to read from)
- **Rule: W + R > N** guarantees that at least one node in the read set has the latest written value → **strong consistency**.
- **Typical configs:** N=3, W=2, R=2 (strong). N=3, W=1, R=1 (fast but eventually consistent).

## 6. Optimizations

### Optimization 1: Vector Clocks for Conflict Resolution
When two nodes independently update the same key during a network partition, we have a conflict. **Vector clocks** track the version history from each node's perspective. Upon reconciliation, the system detects that neither version is an ancestor of the other → **conflict**.

Resolution strategies:
- **Last-Write-Wins (LWW):** Use wall-clock timestamp. Simple but can lose data.
- **Application-level merge:** Present both versions to the application. Amazon's shopping cart uses this: merge both carts (customer never loses items).

### Optimization 2: Gossip Protocol for Failure Detection
Nodes periodically (every 1 second) exchange **heartbeat metadata** with randomly selected peers. Each heartbeat message contains a list of known nodes and their last-seen timestamps. If a node hasn't been mentioned in gossip messages for 30 seconds, it is suspected failed. This is decentralized — no single failure detector that becomes a SPOF.

### Optimization 3: Merkle Trees for Anti-Entropy Repair
Each node maintains a **Merkle tree** (hash tree) over its key range. The root hash represents the fingerprint of all data on that node. To detect out-of-sync replicas:
1. Two nodes compare root hashes. If identical, they are in sync.
2. If different, recursively compare child hashes to identify exactly which key ranges are out of sync.
3. Transfer only the out-of-sync keys. This minimizes data transfer during repairs.

### Optimization 4: Hinted Handoff
If a replica node is temporarily down during a write, the coordinator writes to another healthy node instead, attaching a "hint" (metadata saying "this data belongs to Node C"). When Node C recovers, the healthy node replays the hinted data to it and deletes the hint. This ensures writes are never rejected due to temporary node failures.

### Optimization 5: Read Repair
When a read returns values from R replicas, the coordinator might notice that one replica has stale data (older version). The coordinator asynchronously sends the latest value to the stale replica, silently repairing the inconsistency. Every read operation doubles as an anti-entropy mechanism.
    `
    },

    // ─── 7. Web Crawler ─────────────────────────────────────────
    {
        id: 'design-web-crawler',
        title: 'Design a Web Crawler (Google)',
        difficulty: 'Hard',
        summary: 'BFS-based crawling, URL frontier with priority and politeness, Bloom filter deduplication, content fingerprinting, and distributed architecture.',
        content: `
## 1. Functional Requirements
1. Given a set of **seed URLs**, crawl the web by downloading HTML pages.
2. **Extract hyperlinks** from each downloaded page and add them to the crawl queue.
3. **Store** the downloaded content for downstream processing (indexing, ML, analytics).
4. **Respect robots.txt** — obey website-specific crawl rules and disallowed paths.
5. **Detect and avoid duplicate content** — many URLs serve identical pages.

## 2. Non-Functional Requirements
1. **Scalability:** Crawl **1 billion pages per month** (~400 pages/second sustained).
2. **Politeness:** Do NOT overwhelm any single web server. Enforce minimum delay (e.g., 1 second) between requests to the same domain. Only one concurrent request per domain.
3. **Robustness:** Handle spider traps (infinite URL generation), malformed HTML, server errors, extremely large pages, and redirect loops.
4. **Extensibility:** Easy to add support for new content types (PDFs, images, JavaScript-rendered pages).
5. **Freshness:** Important pages (news sites) should be re-crawled frequently; static pages (Wikipedia archives) can be re-crawled monthly.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **URL** | url, domain, priority, depth, last_crawled, status | A URL in the frontier |
| **Page** | url, html_content, headers, content_hash, crawled_at | Downloaded page |
| **RobotsRule** | domain, disallowed_paths[], crawl_delay_seconds | Cached robots.txt |

## 4. API Design
This is an **internal system**, not a user-facing API. It runs autonomously.

**Inputs:** Seed URLs (configured via admin dashboard or config file).
**Outputs:** Crawled pages stored in a distributed object store (S3). Metadata indexed in a database for the search indexing pipeline.

## 5. High-Level Design

<diagram>
graph TD
    Seed[Seed URLs] --> Frontier[(URL Frontier)]
    Frontier --> Fetcher[HTML Fetcher Workers]
    Fetcher --> DNS[(DNS Resolver Cache)]
    Fetcher --> Store[(Content Store: S3)]
    Fetcher --> Parser[HTML Parser + Link Extractor]
    Parser --> Dedup{URL Seen Before?}
    Dedup -- No --> Frontier
    Dedup -- Yes --> Drop[Drop Duplicate]
</diagram>

### The URL Frontier (The Heart of the Crawler)
The frontier is NOT a simple queue. It is a sophisticated priority queue with two key properties:

1. **Prioritizer (Front Queue):** Assigns each URL a priority score based on: PageRank, update frequency, domain authority, content freshness. Important pages are crawled first.
2. **Politeness Router (Back Queue):** Maintains a separate FIFO queue per domain. A scheduler ensures only ONE URL from a given domain is fetched at any time, with a minimum delay between fetches. This prevents hammering any single website.

### The Crawl Loop
1. Scheduler picks the highest-priority URL whose domain is not currently being fetched.
2. Fetcher worker downloads the page (respecting the domain's robots.txt rules).
3. HTML Parser extracts all hyperlinks from the page.
4. Each discovered URL passes through the **URL Deduplication** filter.
5. Novel URLs are added back to the Frontier. The loop repeats.

## 6. Optimizations

### Optimization 1: Bloom Filter for URL Deduplication
Before adding a discovered URL to the frontier, check a **Bloom Filter**. A Bloom filter is a probabilistic set that uses constant space regardless of set size. It can tell you "definitely NOT in the set" or "probably in the set." False positives are acceptable (we miss crawling a tiny fraction of URLs). False negatives never happen (we never add a duplicate).

For 10 billion URLs, a Bloom filter needs ~12 GB of memory — far less than storing all URLs in a hash set.

### Optimization 2: Content Fingerprinting (SimHash)
Many different URLs serve the same content (e.g., \\\`/page?sort=asc\\\` vs \\\`/page?sort=desc\\\`). Compute a **SimHash fingerprint** of the HTML body. SimHash produces a 64-bit fingerprint where similar documents have similar fingerprints (differing in only a few bits). If the fingerprint of a newly crawled page matches an existing page within a Hamming distance < 3, mark it as a near-duplicate and skip indexing.

### Optimization 3: Distributed Crawling with Domain Partitioning
Partition domains across crawler workers using consistent hashing on the domain name. Worker 1 handles \\\`*.com\\\` domains A-M, Worker 2 handles N-Z, etc. Each worker maintains its own per-domain politeness queues. This naturally enforces politeness (one worker per domain) and eliminates cross-worker coordination.

### Optimization 4: Dynamic Recrawl Scheduling
Not all pages need to be recrawled at the same frequency. Build a page freshness model:
- Track the page's historical change frequency (from \\\`Last-Modified\\\` headers and content hash comparisons).
- News sites → recrawl every 15 minutes.
- Corporate "About Us" pages → recrawl monthly.
- Assign higher priority in the frontier to pages due for a recrawl.
    `
    },

    // ─── 8. Unique ID Generator ─────────────────────────────────
    {
        id: 'design-unique-id-generator',
        title: 'Design a Unique ID Generator (Twitter Snowflake)',
        difficulty: 'Medium',
        summary: 'Twitter Snowflake architecture for timestamp-sortable, 64-bit distributed IDs: bit layout, clock skew handling, ZooKeeper machine assignment, and pre-generation buffers.',
        content: `
## 1. Functional Requirements
1. Generate **globally unique** IDs across all servers and data centers.
2. IDs must be **sortable by time** — newer IDs must be numerically larger than older ones.
3. IDs should be **64-bit integers** (fit in a standard database BIGINT column).
4. Generate **10,000+ IDs per second per node** without coordination.

## 2. Non-Functional Requirements
1. **High Availability** — ID generation cannot be a single point of failure. If the ID generator goes down, no service can create new records.
2. **Low Latency** — ID generation must complete in < 1 millisecond. It is called inline in every write operation.
3. **No Coordination** — each node must be able to generate IDs independently without talking to other nodes or a central service. Remote calls would add latency and create coupling.
4. **Monotonically Increasing within a node** — IDs from the same node in the same millisecond should be strictly ordered by the sequence counter.

## 3. Core Entities

The core entity is the ID itself — a 64-bit integer with a specific bit layout:

| Bit Range | Bits | Component | Purpose |
|-----------|------|-----------|---------|
| Bit 0 | 1 bit | Sign bit | Always 0 (positive integer) |
| Bits 1-41 | 41 bits | Timestamp | Milliseconds since custom epoch |
| Bits 42-46 | 5 bits | Datacenter ID | Supports 32 data centers (0-31) |
| Bits 47-51 | 5 bits | Machine ID | Supports 32 machines per DC (0-31) |
| Bits 52-63 | 12 bits | Sequence | 4,096 IDs per millisecond per machine |

## 4. API Design
- \\\`GET /api/v1/id\\\` → \\\`{ "id": 123456789012345 }\\\`
- Or, more commonly, exposed as an in-process library call: \\\`long id = IdGenerator.nextId();\\\`
- Batch endpoint: \\\`GET /api/v1/ids?count=100\\\` → returns 100 IDs at once for batch inserts.

## 5. High-Level Design

<diagram>
graph LR
    subgraph 64-bit Snowflake ID Layout
        B1[1 bit Sign]
        B2[41 bits Timestamp]
        B3[5 bits DC ID]
        B4[5 bits Machine ID]
        B5[12 bits Sequence]
    end
    B1 --- B2 --- B3 --- B4 --- B5

    App[Application] --> Gen[ID Generator Library]
    Gen --> |No network call| ID[64-bit Snowflake ID]
</diagram>

### How It Works (Step by Step)
1. The generator reads the **current timestamp** in milliseconds since a custom epoch (e.g., Twitter uses November 4, 2010, 01:42:54 UTC).
2. The **datacenter ID** (5 bits) and **machine ID** (5 bits) are configured at startup — these uniquely identify this specific server instance.
3. The **sequence counter** (12 bits) starts at 0 at the beginning of each millisecond. Each ID generated in the same millisecond increments the counter by 1.
4. If the sequence counter reaches 4,096 (max for 12 bits) within the same millisecond, the generator **busy-waits** until the next millisecond and resets the counter to 0.
5. The final ID is constructed by bitwise OR-ing all components into a single 64-bit integer.

### Capacity Planning
- **41 bits for timestamp:** 2^41 milliseconds = ~69.7 years from the custom epoch. After that, IDs overflow.
- **5 + 5 bits for DC + machine:** 32 x 32 = 1,024 unique workers across all data centers.
- **12 bits for sequence:** 4,096 IDs per millisecond = **~4 million IDs per second per machine.** More than enough for most use cases.

### Why NOT Auto-Increment or UUID?
- **Auto-Increment:** Requires a single-point-of-truth database. That database becomes a bottleneck and a single point of failure. Also, sharding a single auto-increment sequence across multiple databases is extremely complex.
- **UUID v4 (128-bit random):** Not time-sortable. Too large (128 bits vs 64 bits) — bloats database indexes and reduces B-tree cache efficiency. Poor cache locality due to randomness.

## 6. Optimizations

### Optimization 1: Clock Skew Protection
If the system clock jumps backward (due to NTP synchronization correction), the generator MUST refuse to generate IDs until the clock catches up to the last-seen timestamp. Otherwise, the timestamp component would decrease, violating the sortability guarantee and potentially generating duplicate IDs.

Implementation: store \\\`lastTimestamp\\\`. On each call, if \\\`currentTimestamp < lastTimestamp\\\`, throw an error or busy-wait until the clock recovers.

### Optimization 2: ZooKeeper for Machine ID Assignment
Manually configuring unique machine IDs across 1,024 servers is error-prone. Use **ZooKeeper** to dynamically assign machine IDs:
1. On startup, the server creates an ephemeral sequential ZooKeeper node.
2. ZooKeeper assigns it a unique sequence number (0-1023).
3. This sequence number becomes the server's machine ID.
4. If the server crashes, the ephemeral node is deleted and the ID becomes available for reuse.

### Optimization 3: Pre-Generation Buffer
For burst-heavy workloads (e.g., a batch ETL job that inserts 100K records at once), generating IDs synchronously is a bottleneck. Maintain a **pre-generated circular buffer** of ~10,000 IDs. A background thread continuously fills the buffer. The application thread grabs IDs from the buffer without any computation. This achieves near-zero latency for ID generation.

### Optimization 4: Multi-Segment Custom Epoch
Different teams within an organization can use different custom epochs to extend the 69-year lifespan. Alternatively, reduce the machine/DC bits if you have fewer than 1,024 machines and allocate the saved bits to the timestamp for a longer lifespan.
    `
    },

    // ─── 9. Video Streaming (YouTube) ───────────────────────────
    {
        id: 'design-youtube',
        title: 'Design YouTube (Video Streaming)',
        difficulty: 'Hard',
        summary: 'Video upload pipeline, adaptive bitrate streaming (ABR with HLS/DASH), CDN edge caching, transcoding DAG architecture, and pre-signed URL uploads.',
        content: `
## 1. Functional Requirements
1. Users can **upload videos** (files up to 10 GB, any common format).
2. Users can **stream videos** smoothly with adaptive quality (auto-adjusts resolution based on bandwidth).
3. Users can **search** for videos by title, tags, and description.
4. Each video has a **detail page** with title, description, view count, likes, and comments.
5. **Subscribe** to channels and receive notifications on new uploads.

## 2. Non-Functional Requirements
1. **Smooth Playback** — zero buffering on a stable connection. The video player must adapt to bandwidth changes within 2-3 seconds.
2. **Upload Processing** — a 1 GB video should be fully processed (transcoded to all formats) within 10 minutes.
3. **High Availability** — streaming must be 99.99% available. Downtime means millions of viewers staring at a blank screen.
4. **Global Low Latency** — viewers in Tokyo, São Paulo, and London all need < 200ms time-to-first-byte. This requires a CDN.
5. **Scale:** 500 hours of video uploaded per minute (YouTube's actual rate), 1 billion hours watched per day.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **Video** | video_id, uploader_id, title, description, tags[], status (processing/ready), created_at | Video metadata |
| **VideoAsset** | video_id, resolution (1080p/720p/480p/360p), codec (H.264/VP9), storage_url, file_size | Each transcoded variant |
| **Channel** | channel_id, owner_id, name, subscriber_count | Creator's channel |
| **Comment** | comment_id, video_id, user_id, text, created_at | User comment |

## 4. API Design
- \\\`POST /api/v1/videos/upload-url\\\` → { uploadUrl: "https://s3.pre-signed-url...", videoId } (client uploads directly to S3 via pre-signed URL)
- \\\`POST /api/v1/videos/{videoId}/metadata\\\` — { title, description, tags, thumbnail }
- \\\`GET /api/v1/videos/{videoId}\\\` → metadata + streaming manifest URL
- \\\`GET /api/v1/videos/{videoId}/stream/manifest.m3u8\\\` → HLS manifest
- \\\`GET /api/v1/feed?userId=X\\\` → subscription feed

## 5. High-Level Design

<diagram>
graph TD
    Upload[User Upload] -->|Pre-Signed URL| S3Raw[(S3: Raw Video)]
    S3Raw -->|Event Trigger| DAG[Transcoding DAG Pipeline]
    
    DAG --> Split[Video Splitter]
    Split --> T1[Transcode: 1080p]
    Split --> T2[Transcode: 720p]
    Split --> T3[Transcode: 480p]
    T1 --> Merge[Merge + Generate Manifest]
    T2 --> Merge
    T3 --> Merge
    Merge --> S3Proc[(S3: Processed Videos)]

    Viewer((Viewer)) --> CDN[CDN Edge Server]
    CDN -->|Cache Miss| S3Proc
</diagram>

### Video Upload Flow
1. Client requests a pre-signed S3 upload URL from the API server. This prevents the video binary from flowing through our application servers.
2. Client uploads the raw video directly to S3.
3. S3 emits an event (via SNS/SQS) that triggers the **Transcoding DAG Pipeline**.
4. The pipeline splits the raw video into small chunks (e.g., 6-second segments), transcodes each chunk in parallel to multiple resolutions (1080p, 720p, 480p, 360p), and generates an **HLS manifest (.m3u8)** listing all segments and available quality levels.
5. Processed assets are stored back in S3. The video's metadata status is updated to "ready."

### Video Streaming Flow (Adaptive Bitrate)
1. Viewer clicks play → client fetches the HLS manifest.
2. The manifest lists all available quality levels and segment URLs.
3. The video player measures current bandwidth and selects the highest quality that can be streamed without buffering.
4. Every 6 seconds (segment boundary), the player re-evaluates bandwidth and may switch quality seamlessly (e.g., 1080p → 480p if bandwidth drops).
5. Segments are served from the nearest **CDN edge server**. On cache miss, the CDN pulls from S3 origin.

## 6. Optimizations

### Optimization 1: DAG-Based Parallel Transcoding
A naive linear pipeline (split → transcode 1080p → transcode 720p → ...) is slow. Instead, model the pipeline as a **Directed Acyclic Graph (DAG)**: the split step fans out to all transcoding tasks in parallel. A single 1 GB video that takes 30 minutes sequentially can be processed in 8-10 minutes with parallel transcoding across multiple worker nodes.

### Optimization 2: CDN Pre-Push for Popular Content
For highly anticipated videos (a popular creator's new upload), the CDN can pro-actively **push** segments to edge locations before viewers even request them. Monitor analytics: if a video reaches 10K views in the first minute, classify it as "viral" and push to all major CDN PoPs worldwide. This reduces cache miss rates during viral spikes from 80% to near 0%.

### Optimization 3: Smart Thumbnail Generation
Instead of asking uploaders to select a thumbnail, automatically extract 3-5 candidate frames from the video (diverse, high-quality, non-blurry frames using an ML model). The uploader picks from these candidates or the system selects the best one automatically. This significantly improves click-through rates.

### Optimization 4: Resumable Uploads with tus Protocol
Uploading a 10 GB file on a flaky mobile connection will fail frequently. Use the **tus resumable upload protocol**: the client uploads in chunks (e.g., 5 MB each), and the server tracks which chunks have been received. On failure, the client resumes from the last successful chunk instead of restarting the entire upload.

### Optimization 5: Cost Optimization — Tiered Storage
Store the original 4K master in S3 Standard. After 30 days, if a video has fewer than 100 views, automatically transition lower-priority transcodes (360p, 480p) to S3 Glacier (90% cheaper). On-demand re-transcode if a cold video suddenly goes viral. Most videos on YouTube receive > 90% of their lifetime views in the first 48 hours.
    `
    },

    // ─── 10. Typeahead / Autocomplete ───────────────────────────
    {
        id: 'design-typeahead',
        title: 'Design Typeahead / Autocomplete (Google Search)',
        difficulty: 'Medium',
        summary: 'Trie data structure for prefix search, ranking by search frequency, data collection pipeline, memory optimization with node pruning, and AJAX debouncing.',
        content: `
## 1. Functional Requirements
1. As the user types each character, display a dropdown of the **top 5-10 search suggestions** matching the current prefix.
2. Suggestions are **ranked by popularity** (search frequency), not alphabetically.
3. Support for **multi-language** queries (not just English).
4. The system returns results based on **prefix matching**, not substring or fuzzy matching (for simplicity).

## 2. Non-Functional Requirements
1. **Ultra-Low Latency** — suggestions must appear within 100ms of each keystroke. Beyond 200ms, the feature feels sluggish and users stop relying on it.
2. **High Availability** — the typeahead service should be 99.99% available. Users type on every search — if autocomplete is down, the search experience is noticeably degraded.
3. **Scalability** — handle 5 billion search queries per day (Google's scale). Even if only 60% of keystrokes trigger an autocomplete request, that is ~10 trillion characters processed daily.
4. **Freshness** — trending queries (breaking news, viral events) should appear in suggestions within minutes, not hours.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **TrieNode** | children (map of char → TrieNode), top_queries (sorted list of top K by frequency) | Core data structure |
| **QueryLog** | query_text, timestamp, region, count | Raw search logs |
| **FrequencyTable** | prefix, query, frequency | Aggregated query frequencies |

## 4. API Design
- \\\`GET /api/v1/suggest?q=des&limit=5\\\` → \\\`["design patterns", "design thinking", "desktop wallpaper", "design system", "desert eagle"]\\\`

A single endpoint. The response must be **< 100ms.** Use AJAX calls with **debouncing** (fire request only after 50ms of no further keystrokes) to reduce request volume by ~70%.

## 5. High-Level Design

<diagram>
graph TD
    User((User)) -->|Keystroke| WebApp[Web Application]
    WebApp -->|Debounced AJAX| LB[Load Balancer]
    LB --> TrieSvc1[Trie Server 1]
    LB --> TrieSvc2[Trie Server 2]
    TrieSvc1 --> TrieMem[(In-Memory Trie)]
    TrieSvc2 --> TrieMem2[(In-Memory Trie)]

    Logs[Search Query Logs] --> Agg[Aggregation Pipeline]
    Agg --> FreqDB[(Frequency Database)]
    FreqDB -->|Weekly Rebuild| TrieSvc1
    FreqDB -->|Weekly Rebuild| TrieSvc2
</diagram>

### Two Separate Subsystems
**1. Data Collection Pipeline (Offline):**
- All search queries are logged.
- An aggregation pipeline (Hadoop/Spark) runs weekly: counts the frequency of each query and computes the top K suggestions for every possible prefix.
- The results are serialized into a Trie snapshot and deployed to Trie Servers.

**2. Query Serving Path (Online):**
- User keystroke → debounced AJAX request → Load Balancer → Trie Server.
- Trie Server looks up the prefix in its in-memory Trie → returns the top 5 pre-computed suggestions.
- Response time: < 10ms (it is entirely an in-memory lookup).

### The Trie Data Structure
A Trie (prefix tree) stores characters at each node. Traversing from root to any node spells out a prefix. At each node, we pre-compute and cache the **top K query suggestions** for that prefix:
- Node for "d" → top 5 queries starting with "d"
- Node for "de" → top 5 queries starting with "de"
- Node for "des" → top 5 queries starting with "des"

This makes query serving O(L) where L is the prefix length — typically 3-10 characters. Sub-millisecond.

## 6. Optimizations

### Optimization 1: Trie Sharding by Prefix
A single Trie for all queries won't fit on one server. Shard: Server 1 handles prefixes "a"-"g", Server 2 handles "h"-"n", etc. The load balancer routes requests to the correct shard based on the first character. Each shard fits entirely in memory.

### Optimization 2: Sampling for Data Collection
Logging EVERY keystroke of EVERY search is enormous (trillions of events per day). Instead, **sample 1 in 1000 queries** for frequency counting. At Google's scale, a 0.1% sample still yields billions of data points — statistically sufficient for accurate frequency estimation.

### Optimization 3: Browser Caching
Once the user types "des" and gets suggestions, cache the response in the browser. If the user continues to "desi", the client can filter the cached "des" results locally instead of making another server round-trip. Only fetch from the server when the cached prefix changes. This eliminates 50-70% of autocomplete requests.

### Optimization 4: Trending Query Injection
The weekly-rebuilt Trie doesn't capture real-time trends. Run a separate **real-time trending pipeline** (using a stream processor like Flink) that detects queries spiking in the last 60 minutes. Inject these trending queries into the Trie servers' in-memory cache with boosted scores. This ensures breaking news appears in suggestions within minutes.
    `
    },

    // ─── 11. Pastebin ───────────────────────────────────────────
    {
        id: 'design-pastebin',
        title: 'Design Pastebin',
        difficulty: 'Easy',
        summary: 'Content-addressable storage, object storage for large pastes, metadata vs content separation, TTL-based expiry, and syntax highlighting support.',
        content: `
## 1. Functional Requirements
1. Users can create a **paste** — a block of text (up to 10 MB) — and receive a unique shareable URL.
2. Users accessing the URL can **read** the paste content.
3. Pastes can be set to **expire** after a configurable duration (10 min, 1 hour, 1 day, 1 week, never).
4. Optional: **syntax highlighting** based on detected or user-specified language.
5. Optional: **password protection** — viewing the paste requires entering a password.

## 2. Non-Functional Requirements
1. **High Availability** — shared paste URLs are embedded in documentation, Stack Overflow answers, and chat messages worldwide. They must always be accessible.
2. **Low Latency Read** — paste content should load in < 200ms.
3. **Durability** — once a paste is created, its content must not be lost until it expires (or is deleted).
4. **Scale Estimates:**
   - **Writes:** 5M new pastes/day = ~60 pastes/sec. Read-heavy: 50:1 read/write ratio = 3,000 reads/sec.
   - **Storage:** Average paste 10 KB. 5M/day x 365 days x 10 KB = **~18 TB/year.**

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **Paste** | paste_id (hash), title, language, is_private, password_hash, created_at, expires_at, size_bytes, storage_key | Metadata |
| **PasteContent** | storage_key → raw text blob | Stored in object storage (S3) — NOT in the metadata DB |

### Key Design Decision: Separate Metadata from Content
Paste content can be up to 10 MB. Storing it directly in the relational database would bloat the DB, slow down queries, and make backups expensive. Instead:
- **Metadata** (title, expiry, language) → stored in a relational DB (PostgreSQL) or key-value store.
- **Content** (the actual text) → stored in an **object store** (S3 or MinIO). The metadata record holds a \\\`storage_key\\\` that references the S3 object.

## 4. API Design
- \\\`POST /api/v1/pastes\\\` — Body: { content, title?, language?, expiresIn?, password? } → { pasteUrl, pasteId }
- \\\`GET /api/v1/pastes/{pasteId}\\\` → { metadata, content } (verifies password if private)
- \\\`DELETE /api/v1/pastes/{pasteId}\\\` → 204 (only by the creator)

## 5. High-Level Design

<diagram>
graph TD
    Client((User)) --> LB[Load Balancer]
    LB --> API[API Server]

    API -->|Write Metadata| MetaDB[(PostgreSQL)]
    API -->|Write Content| S3[(S3 Object Store)]
    API -->|Read Cache| Redis[(Redis Cache)]

    Redis -->|Cache Miss Metadata| MetaDB
    Redis -->|Cache Miss Content| S3

    Cleanup[TTL Cleanup Cron] --> MetaDB
    Cleanup --> S3
</diagram>

### Write Flow
1. Client sends the paste content + metadata.
2. API server generates a unique paste_id (Base62-encoded, 8 characters = 62^8 = 218 billion combinations).
3. Content is uploaded to S3 with key \\\`pastes/{paste_id}\\\`.
4. Metadata is stored in PostgreSQL.
5. Return the paste URL.

### Read Flow
1. Client accesses \\\`GET /pastes/{pasteId}\\\`.
2. API first checks Redis for cached metadata + content.
3. On cache miss: fetch metadata from PostgreSQL, fetch content from S3.
4. Store both in Redis with a short TTL (e.g., 15 minutes) for subsequent reads.
5. Return the paste to the client with syntax highlighting applied server-side or client-side.

## 6. Optimizations

### Optimization 1: Content-Addressable Storage (Deduplication)
Many pastes contain identical content (common code snippets, configuration templates). Hash the paste content (SHA-256) and use the hash as the S3 object key. Before uploading, check if the hash already exists in S3. If it does, skip the upload and just create a new metadata record pointing to the existing S3 object. This can reduce storage by 20-30%.

### Optimization 2: Tiered Expiry Cleanup
Running a single cron job that scans the entire metadata table for expired pastes is inefficient at scale. Instead:
- Use a **PostgreSQL partitioned table** partitioned by expiry date (one partition per day).
- To delete expired pastes: simply drop the partition for yesterday. This is an O(1) operation regardless of partition size.

### Optimization 3: CDN for Frequently Accessed Pastes
Put a CDN (CloudFront) in front of the S3-backed read path. Popular pastes linked from Stack Overflow or Hacker News receive thousands of concurrent reads. Serving from CDN edge caches reduces S3 egress costs and latency.

### Optimization 4: Rate Limiting to Prevent Abuse
Without rate limiting, spammers will use Pastebin as a free hosting platform for malware, phishing pages, or illegal content. Implement:
- Per-IP rate limit: max 30 pastes per hour.
- Max paste size: 10 MB hard limit.
- Content scanning: a background worker checks new pastes against malware/phishing signatures.
    `
    },

    // ─── 12. Instagram ──────────────────────────────────────────
    {
        id: 'design-instagram',
        title: 'Design Instagram (Photo Sharing)',
        difficulty: 'Hard',
        summary: 'Image upload pipeline with CDN distribution, news feed generation, follow graph, like/comment interactions, Explore page ranking, and Stories with TTL.',
        content: `
## 1. Functional Requirements
1. **Upload photos** with captions, tags, and location data.
2. **Follow** other users to see their posts in a personal feed.
3. **Like and comment** on posts.
4. **Explore page** — discover popular and relevant content from accounts you do not follow.
5. **Stories** — ephemeral content that disappears after 24 hours.
6. **Direct Messages** — private messaging between users.

## 2. Non-Functional Requirements
1. **Highly Available** — users should always be able to view and post content.
2. **Feed Latency** — home feed must load in < 300ms, including image thumbnails.
3. **Upload Latency** — photo should appear in the user's profile within 5 seconds of upload completion.
4. **Eventual Consistency** — a new post may take 10-30 seconds to appear in all followers' feeds (acceptable).
5. **Scale Estimates:**
   - **2 billion MAU**, 500M DAU, 100M photos uploaded per day.
   - Average photo: 2 MB raw, 200 KB optimized (multiple resolutions).
   - **Storage growth:** 100M × 200 KB × 4 resolutions = ~80 TB/day of processed images.
   - **100 billion photos** in cold storage after 3 years.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **User** | user_id, username, bio, avatar_url, follower_count, following_count | User profile |
| **Post** | post_id, user_id, caption, location, image_urls[], created_at | A photo post |
| **Follow** | follower_id, followee_id, created_at | Social graph |
| **Like** | user_id, post_id, created_at | Like event |
| **Comment** | comment_id, post_id, user_id, text, created_at | User comment |
| **Story** | story_id, user_id, media_url, created_at, expires_at | Ephemeral content (24h) |

## 4. API Design
- \\\`POST /api/v1/posts\\\` — multipart form: { image, caption, location, tags[] } → { postId, imageUrls }
- \\\`GET /api/v1/feed?cursor=last_post_id&limit=20\\\` — paginated home feed
- \\\`POST /api/v1/posts/{id}/like\\\` — like a post
- \\\`GET /api/v1/explore?page=1\\\` — explore page with ranked popular content
- \\\`POST /api/v1/stories\\\` — upload a story (auto-expires in 24h)
- \\\`GET /api/v1/stories/{userId}\\\` — get user's active stories

## 5. High-Level Design

<diagram>
graph TD
    User((User)) --> LB[Load Balancer]
    LB --> PostSvc[Post Service]
    LB --> FeedSvc[Feed Service]
    LB --> StorySvc[Story Service]

    PostSvc -->|Pre-Signed URL| S3[(S3: Image Store)]
    PostSvc --> ImgPipe[Image Processing Pipeline]
    ImgPipe --> S3
    S3 --> CDN[Global CDN]

    PostSvc --> PostDB[(Post Metadata DB)]
    PostSvc --> Fanout[Feed Fanout]
    Fanout --> FeedCache[(Redis: Feed Cache)]

    FeedSvc --> FeedCache
    FeedSvc --> PostDB

    StorySvc --> StoryDB[(Redis with TTL: Stories)]
</diagram>

### Photo Upload Pipeline
1. Client requests a pre-signed S3 URL and uploads the raw image directly.
2. S3 upload triggers the **Image Processing Pipeline** via event notification.
3. Pipeline generates multiple resolutions (original, 1080px, 640px, 320px thumbnail) and formats (WebP for Android, HEIC for iOS).
4. Processed images are stored back in S3 and pushed to CDN edge servers.
5. Post metadata (caption, image URLs) is saved to the Post DB.
6. Feed Fanout service pushes the post_id into followers' Redis feed caches.

### Feed Architecture
Uses the same **hybrid fan-out** model as the News Feed question:
- Normal users (< 50K followers): fan-out-on-write.
- Celebrity accounts (> 50K followers): fan-out-on-read at query time.

## 6. Optimizations

### Optimization 1: Image Optimization Pipeline
Raw photos from modern smartphones are 8-15 MB. Processing steps:
- **Resize** to 4 standard resolutions (original, 1080, 640, 320).
- **Convert** to WebP (30% smaller than JPEG at equivalent quality).
- **Strip EXIF metadata** for privacy (GPS coordinates, camera model).
- **Apply CDN cache headers** (max-age=31536000, immutable — images are immutable once processed).

### Optimization 2: Stories with Redis TTL
Stories expire after 24 hours. Store them in **Redis** with a native TTL of 86,400 seconds. Redis automatically evicts expired stories — no cleanup cron job needed. Each user's stories are a Redis Sorted Set keyed by user_id, with story entries scored by created_at.

### Optimization 3: Explore Page Ranking
The Explore page surfaces content from accounts the user does NOT follow. Ranking signals:
- **Engagement velocity** — posts getting rapid likes/comments in the first hour.
- **Content similarity** — ML model embeds images into a vector space; recommend images similar to those the user has liked.
- **Diversity** — inject posts from different categories to avoid filter bubbles.
Pre-compute candidate posts hourly using a batch pipeline. Re-rank in real-time based on the requesting user's interest profile.

### Optimization 4: Sharding Strategy
Shard the Post DB by user_id. This means all posts by a single user live on the same shard — efficient for profile page queries. The Feed Cache (Redis) is sharded by the feed-owner's user_id. The Social Graph (Follows) is sharded by follower_id for efficient "who do I follow?" queries.

### Optimization 5: Like Count with Redis + Async Sync
Like counts are extremely high-throughput (thousands of likes per second on popular posts). Use Redis INCR for real-time counting, and asynchronously sync the count to the persistent database every 30 seconds. If Redis fails, the database count is the fallback (slightly stale but never lost).
    `
    },

    // ─── 13. Ride-Sharing (Uber/Lyft) ──────────────────────────
    {
        id: 'design-uber',
        title: 'Design Uber / Lyft (Ride Sharing)',
        difficulty: 'Hard',
        summary: 'Geospatial indexing with GeoHash/QuadTree, real-time driver matching, trip lifecycle state machine, ETA computation with Dijkstra, and surge pricing.',
        content: `
## 1. Functional Requirements
1. **Riders** can request a ride by entering a pickup and drop-off location.
2. The system **matches** the rider with the nearest available driver.
3. **Drivers** can toggle online/offline status and receive ride requests.
4. Both parties see a **real-time map** showing each other's location during the trip.
5. **Price estimation** before booking (based on distance, time, and demand).
6. **Payment processing** — automatic charge at the end of the trip.
7. **Trip history** — both riders and drivers can review past trips.

## 2. Non-Functional Requirements
1. **Low-Latency Matching** — a rider should be matched with a driver within 10 seconds. Every second of waiting reduces conversion.
2. **Real-Time Location Updates** — driver GPS positions should update every 3-5 seconds for smooth map rendering.
3. **High Availability** — 99.99% uptime. If the matching service goes down, no rides can be booked, and millions of dollars in revenue are lost per hour.
4. **Strong Consistency for Matching** — a driver must NEVER be matched with two riders simultaneously. This requires distributed locking or careful state machine management.
5. **Scale Estimates:**
   - **20M daily active riders**, 5M daily active drivers.
   - **1M concurrent active drivers** sending GPS updates every 3 seconds = 333K location updates per second.
   - **Peak:** 100K ride requests per minute during rush hours.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **Rider** | user_id, name, payment_method_id, rating | Passenger account |
| **Driver** | driver_id, name, vehicle_info, current_location, status (offline/available/en_route/on_trip), rating | Driver account with live status |
| **Trip** | trip_id, rider_id, driver_id, pickup, dropoff, status (requested/matched/driver_arriving/in_progress/completed/cancelled), estimated_fare, actual_fare, created_at | A single ride |
| **Location** | driver_id, latitude, longitude, timestamp, geohash | Real-time driver position |

## 4. API Design
- \\\`POST /api/v1/rides/request\\\` — { pickupLat, pickupLng, dropoffLat, dropoffLng } → { tripId, estimatedFare, etaSeconds }
- \\\`POST /api/v1/rides/{tripId}/accept\\\` — driver accepts ride
- \\\`PUT /api/v1/drivers/location\\\` — { lat, lng } (called every 3 seconds from driver app)
- \\\`PUT /api/v1/drivers/status\\\` — { status: "available" | "offline" }
- \\\`GET /api/v1/rides/{tripId}\\\` — trip details with live driver location
- \\\`POST /api/v1/rides/{tripId}/complete\\\` — end the trip, trigger payment

Real-time location streaming uses **WebSocket** or **gRPC server streaming**, not polling REST.

## 5. High-Level Design

<diagram>
graph TD
    Rider((Rider App)) --> LB[Load Balancer]
    Driver((Driver App)) -->|GPS every 3s| LocSvc[Location Service]
    LB --> RideSvc[Ride Service]
    LB --> MatchSvc[Matching Service]

    LocSvc --> LocStore[(Redis GeoHash Store)]
    MatchSvc --> LocStore
    MatchSvc --> DriverQ[Driver Notification Queue]

    RideSvc --> TripDB[(Trip Database)]
    RideSvc --> PaySvc[Payment Service]
    PaySvc --> Stripe[Stripe / Payment Gateway]
</diagram>

### Location Service: Handling 333K Updates/Second
Drivers send GPS coordinates every 3 seconds. The Location Service updates the driver's position in a **Redis GeoHash store** using \\\`GEOADD drivers {lng} {lat} {driverId}\\\`. Redis Geo operations are O(log(N)) and can handle millions of entries.

### Matching Algorithm (The Core)
1. Rider requests a ride from location (lat, lng).
2. Matching Service queries Redis: \\\`GEORADIUS drivers {lng} {lat} 5 km COUNT 20 ASC\\\` — returns the 20 nearest available drivers within 5 km, sorted by distance.
3. Filter out drivers whose status is not "available."
4. Send a ride request to the nearest driver. If they don't accept within 15 seconds, try the next nearest driver.
5. Once accepted, atomically update the driver's status to "en_route" and create the Trip record.

### Trip State Machine
\\\`REQUESTED → MATCHED → DRIVER_ARRIVING → IN_PROGRESS → COMPLETED\\\`
(or \\\`CANCELLED\\\` from any state)

Each state transition is persisted to the Trip DB. The state determines what each party sees in the app (e.g., "Driver is arriving" vs "Trip in progress").

## 6. Optimizations

### Optimization 1: GeoHash-Based Sharding
Partition the location database by **GeoHash prefix** (e.g., the first 4 characters). Each shard covers a specific geographic region. Matching queries only hit the shard(s) covering the rider's region plus adjacent regions (to handle cross-boundary cases). This keeps each shard small and reduces query latency.

### Optimization 2: ETA with Road-Weighted Graphs
Simple Haversine distance (straight-line) is inaccurate for ETA. Use a pre-computed road network graph with edge weights (traffic speed per segment). Run **Dijkstra's algorithm** (or A* for better performance) on the graph, updated with real-time traffic data from driver GPS traces. Partition the graph into geographic tiles for parallelism.

### Optimization 3: Surge Pricing
Monitor the ratio of ride requests to available drivers per GeoHash cell. If demand/supply > 2x, apply a surge multiplier (1.5x-3x). Recalculate every 2 minutes. Display the multiplier to riders before they confirm booking — this simultaneously increases driver supply (drivers see higher payouts) and reduces rider demand (some riders choose to wait).

### Optimization 4: Driver Matching Radius Expansion
If no drivers are found within 5 km, gradually expand the search radius: 5 km → 8 km → 12 km, with 10-second waits between expansions. If no driver is found within 15 km after 60 seconds, inform the rider that no drivers are available.

### Optimization 5: Supply Prediction with Heat Maps
Use historical trip data to predict demand hotspots 30 minutes into the future (ML model trained on time-of-day, day-of-week, events, weather). Show predicted demand heat maps to drivers, incentivizing them to reposition to high-demand areas before the surge begins. This reduces matching time and wait times.
    `
    },

    // ─── 14. Google Drive / Dropbox ─────────────────────────────
    {
        id: 'design-google-drive',
        title: 'Design Google Drive / Dropbox (Cloud Storage)',
        difficulty: 'Hard',
        summary: 'Block-level file chunking, deduplication, delta sync, conflict resolution with OT/CRDT, offline mode with reconciliation, and versioning.',
        content: `
## 1. Functional Requirements
1. **Upload and download files** (documents, photos, videos) up to 50 GB.
2. **Automatic sync** — changes made on one device appear on all linked devices.
3. **File sharing** — share files/folders with specific users via link or email.
4. **Version history** — view and restore previous versions of any file (last 30 versions).
5. **Offline support** — users can edit files offline; changes sync when connectivity is restored.
6. **Real-time collaboration** — multiple users can edit the same document simultaneously (Google Docs style).

## 2. Non-Functional Requirements
1. **Data Durability** — 99.999999999% (11 nines). Users trust cloud storage with their only copies of important files. Data loss is catastrophic and unrecoverable.
2. **Consistency** — after a sync operation completes, all devices should see the same file state.
3. **Bandwidth Efficiency** — uploading a 1 GB file and changing a single paragraph should NOT re-upload the entire 1 GB. Only the changed portion should be transferred.
4. **Sync Latency** — changes should propagate to other devices within 10 seconds on a good connection.
5. **Scale:**
   - **1 billion users**, 500M daily active.
   - Average user stores 5 GB = **5 exabytes total storage**.
   - **10 billion files** total across all users.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **User** | user_id, email, storage_quota, used_storage | Account |
| **File** | file_id, owner_id, name, path, size, checksum, current_version, is_folder, created_at, updated_at | File/folder metadata |
| **FileVersion** | version_id, file_id, version_number, block_list[], checksum, created_by, created_at | Version history with block references |
| **Block** | block_hash (SHA256), size, storage_url | Content-addressable block in object storage |
| **Share** | file_id, shared_with_user_id, permission (view/edit), created_at | Sharing permissions |

### Key Design Insight: Block-Level Storage
Files are NOT stored as monolithic blobs. Each file is split into fixed-size **blocks** (typically 4 MB). Blocks are stored in object storage (S3) and addressed by their SHA-256 hash (content-addressable storage). A file is just a list of block hashes.

**Why?**
- **Deduplication:** Common blocks (e.g., empty padding, standard file headers) are stored only once, even across different users' files. At scale, this saves petabytes.
- **Delta Sync:** When a user edits a file, only the changed blocks are re-uploaded — not the entire file.
- **Versioning:** Each version is a different list of block hashes. Unchanged blocks between versions share the same storage.

## 4. API Design
- \\\`POST /api/v1/files/upload-init\\\` — { name, path, size, blockChecksums[] } → { fileId, blocksToUpload[] }
  (Server checks which blocks already exist; client only uploads novel blocks)
- \\\`PUT /api/v1/blocks/{blockHash}\\\` — upload a single 4 MB block
- \\\`POST /api/v1/files/{fileId}/commit\\\` — { versionNumber, blockList[] } (finalize upload)
- \\\`GET /api/v1/files/{fileId}/download\\\` → block URLs for parallel download
- \\\`GET /api/v1/sync?lastSyncTimestamp=T\\\` → { changes: [created, updated, deleted files since T] }

## 5. High-Level Design

<diagram>
graph TD
    Client((Desktop Client)) <-->|WebSocket: Change Notification| NotifSvc[Notification Service]
    Client -->|Upload Blocks| BlockStore[(S3: Block Store)]
    Client --> API[API Server]

    API --> MetaDB[(Metadata DB: PostgreSQL)]
    API --> BlockIdx[(Block Index: Redis)]
    API --> MQ[Kafka: Sync Events]

    MQ --> NotifSvc
    NotifSvc --> Client2((Client Device 2))
    NotifSvc --> Client3((Client Device 3))
</diagram>

### The Sync Flow (Editing a File)
1. User edits a 100 MB file on Device A. The desktop client detects the file change (via filesystem watcher).
2. Client re-chunks the file into 4 MB blocks and computes SHA-256 hashes for each block.
3. Client sends the block hashes to the API: "I have these 25 blocks." The server responds: "I already have 23 of those. Upload only these 2 new blocks."
4. Client uploads only the 2 changed blocks (8 MB instead of 100 MB).
5. Client commits the new version with the updated block list.
6. Server publishes a sync event to Kafka → Notification Service pushes a WebSocket message to all other devices linked to this account.
7. Device B receives the notification, queries the API for the new version's block list, identifies the 2 changed blocks, downloads them, and reconstructs the file locally.

### Conflict Resolution
**Scenario:** Device A and Device B both edit the same file offline, then reconnect.
- **Last-Write-Wins (LWW):** Simple but can lose one user's changes. Used for binary files.
- **Forking:** Keep both versions as separate files (e.g., "Report.docx" and "Report (conflict).docx"). Let the user manually merge. Dropbox uses this approach.
- **Operational Transform (OT) / CRDT:** For real-time collaborative text editing (Google Docs). Operations (insert char at position 5, delete char at position 10) are transformed to preserve intent even when applied in different orders.

## 6. Optimizations

### Optimization 1: Delta Sync with Rolling Hash (Rsync Algorithm)
Instead of re-chunking the entire file on every edit, use a **rolling hash** (like Rabin fingerprint) to efficiently identify changed regions. The client computes rolling hashes over a sliding window, compares against the previous version's block hashes, and uploads only the bytes within blocks that differ. This reduces upload bandwidth by 90%+ for small edits to large files.

### Optimization 2: Client-Side Deduplication
Before uploading any block, the client computes its SHA-256 hash and queries the server: "Do you already have this block?" If yes, skip the upload entirely. At scale, 30-40% of blocks are duplicates (common file fragments, document templates, library files). This saves enormous bandwidth and storage.

### Optimization 3: Compression Before Upload
Compress each block with LZ4 (fast) or Zstandard (better ratio) before uploading to S3. Text-heavy files compress 60-80%. Binary files (JPEG, MP4) compress minimally. The client intelligently skips compression for already-compressed file types.

### Optimization 4: Multi-Part Parallel Upload/Download
For large files, upload/download multiple blocks simultaneously (e.g., 4 concurrent transfers). This maximizes bandwidth utilization. Use HTTP Content-Range headers for each block to support pause/resume. A 1 GB file with 4 parallel transfers completes 3-4x faster than sequential.

### Optimization 5: Intelligent Sync Prioritization
Not all files should sync with equal priority:
- Files the user has open → sync immediately (latency-critical).
- Recently edited files → sync within 30 seconds.
- Old, unopened files → sync lazily in the background.
The desktop client maintains a priority queue of pending sync operations, ensuring that the user's active work is always up-to-date first.
    `
    },

    // ─── 15. Ticket Booking (Ticketmaster) ─────────────────────
    {
        id: 'design-ticketmaster',
        title: 'Design a Ticket Booking System (Ticketmaster)',
        difficulty: 'Hard',
        summary: 'Seat reservation with distributed locking, race condition prevention, virtual waiting queues for flash sales, optimistic UI with pessimistic locking, and idempotent payments.',
        content: `
## 1. Functional Requirements
1. Users can browse events and **view available seats** on an interactive seat map.
2. Users can **select seats and complete a booking** (checkout + payment).
3. A seat **cannot be double-booked** (strict inventory consistency). If two users try to book the same seat, exactly one must succeed.
4. **Temporary seat hold** — selected seats are held for 5 minutes during checkout. If payment is not completed, the hold expires and seats become available again.
5. Payment processing and ticket confirmation via email.

## 2. Non-Functional Requirements
1. **Strong Consistency** — two users must NEVER book the same seat. This is non-negotiable. A single double-booking in a production system destroys trust and creates legal/financial liability.
2. **Handle Traffic Spikes** — Taylor Swift concert tickets: 10M users hitting "Buy" simultaneously for 50,000 seats. The system must handle a 1000x traffic surge without crashing.
3. **Low Latency** — seat selection and checkout must feel instant. Users expect sub-second response times even under peak load.
4. **Fairness** — first-come, first-served. No user should be starved.
5. **Scale:** 100M monthly users, 50K events per month, peak traffic of 500K concurrent users during a single high-demand event.

## 3. Core Entities

| Entity | Fields | Notes |
|--------|--------|-------|
| **Event** | event_id, name, venue_id, date, total_seats | An event (concert, game, show) |
| **Seat** | seat_id, event_id, section, row, number, status (available/held/booked), price | Individual seat |
| **Booking** | booking_id, user_id, seat_ids[], status (pending/confirmed/cancelled), created_at, expires_at | A booking (holds seats for 5 min) |
| **Payment** | payment_id, booking_id, amount, status, provider_ref | Payment record |

## 4. API Design
- \\\`GET /api/v1/events/{id}/seats\\\` → { sections: [{ seats: [{ id, status, price }] }] }
- \\\`POST /api/v1/bookings\\\` — { eventId, seatIds[] } → { bookingId, expiresAt } (holds seats for 5 min)
- \\\`POST /api/v1/bookings/{id}/confirm\\\` — { paymentToken } → { ticketPdf }
- \\\`DELETE /api/v1/bookings/{id}\\\` → release held seats

## 5. High-Level Design

<diagram>
graph TD
    Users((10M Users)) --> CDN[CDN: Static Event Pages]
    Users --> LB[Load Balancer]
    LB --> WQ[Virtual Waiting Queue]
    WQ --> BookAPI[Booking API Server]

    BookAPI --> SeatLock[Seat Lock Service]
    BookAPI --> BookDB[(Booking DB: PostgreSQL)]
    BookAPI --> PaySvc[Payment Service]
    BookAPI --> EmailSvc[Email Confirmation Service]

    SeatLock --> Redis[(Redis: Distributed Locks)]
    PaySvc --> Stripe[Stripe / Payment Gateway]
</diagram>

### The Core Challenge: Preventing Double-Booking
When 10M users try to book the same 50,000 seats, we need **pessimistic locking** to guarantee strong consistency.

**The Seat Locking Dance:**
1. User selects Seat A. The API attempts to acquire a **distributed lock** on Seat A in Redis (\\\`SET seat:A:lock user123 EX 300 NX\\\`).
2. If the lock succeeds (NX = only if Not eXists), the seat is "held" for 5 minutes. The user proceeds to payment.
3. If the lock fails (another user already holds it), return "Seat unavailable."
4. On payment success, update the Seat status to "booked" in PostgreSQL and release the Redis lock.
5. If 5 minutes expire without payment, a TTL expiry event releases the lock, making the seat available again.

### Why Redis for Locking?
- Redis \\\`SET ... NX\\\` is **atomic** — only one caller can acquire the lock.
- Redis TTL automatically releases holds after 5 minutes without any cleanup job.
- Redis handles 100K+ operations per second — fast enough for flash sales.

### Virtual Waiting Queue
During high-demand events, letting 10M users simultaneously hit the Booking API would crash the servers. Instead:
1. Users enter a **virtual waiting queue** (backed by a FIFO queue like SQS).
2. Users see "You are #4,521 in line."
3. Only N users at a time are let through to the booking page (N = server capacity, e.g., 5,000 concurrent users).
4. This prevents a thundering herd from crashing the booking servers.

## 6. Optimizations

### Optimization 1: Optimistic UI with Server Validation
Show the seat map to all users immediately (from a cached snapshot). Users can "select" seats instantly on the client side. The actual lock is attempted only when they click "Proceed to Checkout." If the lock fails, show "Seat taken, pick another." This feels fast even under heavy load. The seat map refreshes every 5 seconds to show recently booked seats.

### Optimization 2: Database-Level Pessimistic Locking (Backup)
In addition to Redis locks, use PostgreSQL \\\`SELECT FOR UPDATE\\\` on the Seat row during the final booking confirmation. This is the ultimate safety net: even if Redis and the app layer have a bug, the database refuses to update a seat that is already booked. Two-layer locking ensures zero double-bookings.

### Optimization 3: Read Replicas for Seat Map
The seat map is read millions of times but written to only thousands of times. Serve the seat map from PostgreSQL **read replicas**. Accept that the map is 1-2 seconds stale — the actual lock check at booking time ensures correctness. This reduces load on the primary database by 100x.

### Optimization 4: Idempotent Payment Processing
If a user double-clicks "Pay," the system must not charge twice. Use an **idempotency key** (booking_id) for the payment API call. Stripe and other gateways support this natively. The gateway blocks the second payment attempt with the same idempotency key, returning the result of the first attempt instead.

### Optimization 5: Seat Inventory Partitioning
For very large venues (100K seats), partition the seat inventory by section. Each section has its own Redis lock namespace and its own database partition. This eliminates cross-section lock contention and allows the system to scale horizontally — each section can be served by a different server.
    `
    },
];
